/**
 * The reCAPTCHA score form's rules, free of React so they can be tested alone.
 * Backend reference: docs/RECAPTCHA_SCORE_SETTING.md in ilbp-backend.
 */

export interface RecaptchaSettings {
  scoreThreshold: number;
  setFromDashboard: boolean;
  serverDefault: number;
  min: number;
  max: number;
  updatedAt?: string | null;
  warning?: string | null;
}

/** Plain decimals: `Number` would also take "1e-1" and "0x1", which nobody means here. */
const DECIMAL = /^(\d+(\.\d*)?|\.\d+)$/;

/** The message to put on the input, or an empty string when the value can be saved. */
export function validateThreshold(text: string, min = 0, max = 1): string {
  const value = text.trim();
  if (!value) return "Enter a score.";
  if (!DECIMAL.test(value)) return "Enter a number, for example 0.25.";

  const score = Number(value);
  if (score < min || score > max) return `The score must be from ${min} to ${max}.`;

  return "";
}

export interface ThresholdEffect {
  severity: "info" | "warning" | "error";
  text: string;
}

/**
 * What a score means for the people asking for a code. The cut-offs are advice,
 * not rules: the server takes anything from 0 to 1.
 */
export function describeThreshold(score: number): ThresholdEffect {
  if (score <= 0) {
    return {
      severity: "error",
      text: "Every score passes. Only an invalid token is refused, so there is no protection from scripted sign-ups.",
    };
  }
  if (score < 0.25) {
    return {
      severity: "warning",
      text: "Lenient. Fewer real people are refused, but more scripted requests get a code, and each text costs money.",
    };
  }
  if (score <= 0.5) {
    return {
      severity: "info",
      text: "Balanced. This is the usual range: 0.25 is the server's normal default.",
    };
  }
  if (score < 0.9) {
    return {
      severity: "warning",
      text: "Strict. Real people on low-scoring phones or networks will start being refused a sign-in code.",
    };
  }
  return {
    severity: "error",
    text: "Very strict. Most real people will be refused a sign-in code.",
  };
}

export function describeSource(settings: RecaptchaSettings): string {
  return settings.setFromDashboard
    ? "Set from this page."
    : "Set by the server's configuration. Saving a score here takes over from it.";
}

export const PROPAGATION_NOTICE =
  "A saved score applies at once on the server that took the save, and within about 30 seconds on the others. There is no deploy or restart.";
